import { produce } from 'immer';
import { useCallback, useState } from 'react';

import { UsersTable } from './components/UsersTable';

import { Pagination } from '@/components/Pagination';
import { SearchUsersFallback } from '@/components/SearchUsersFallback';
import { SearchUsersForm } from '@/components/SearchUsersForm';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useSearchUsers } from '@/hooks/useSearchUsers';
import { SearchParams } from '@/services/UserService';

export function UsersView() {
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 1,
    limit: 10,
    filterBy: 'name',
    searchValue: '',
  });

  const { users, isLoading } = useSearchUsers(searchParams);

  function handleSetPerPage(value: number) {
    setSearchParams(
      produce((draft) => {
        draft.page = 1;
        draft.limit = value;
      }),
    );
  }

  function setPageParam(value: number) {
    setSearchParams(
      produce((draft) => {
        draft.page = value;
      }),
    );
  }

  const handleSetSearchParams = useCallback((value: SearchParams) => {
    setSearchParams(value);
  }, []);

  return (
    <div>
      <div className="mb-6 flex flex-row items-end gap-4">
        <div className="w-full">
          <SearchUsersForm
            searchParams={searchParams}
            onSearchParams={handleSetSearchParams}
          />
        </div>

        <div>
          <Select
            value={(searchParams.limit ?? 0).toString()}
            onValueChange={(value) => {
              handleSetPerPage(Number(value));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Items per page" />
            </SelectTrigger>

            <SelectContent>
              {[5, 10, 20, 50].map((n) => (
                <SelectItem key={n} value={n.toString()}>
                  {n} per page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        {users?.items && users.items.length > 0 && (
          <>
            <UsersTable users={users?.items ?? []} isLoading={isLoading} />

            <Pagination
              page={searchParams.page ?? 1}
              totalPages={users?.totalPages ?? 0}
              isLoading={isLoading}
              onPageChange={setPageParam}
            />
          </>
        )}

        {users?.items && users.items.length === 0 && (
          <SearchUsersFallback
            usersCount={users.items.length}
            isLoading={isLoading}
            searchFilter={searchParams.filterBy ?? ''}
            searchValue={searchParams.searchValue ?? ''}
          />
        )}
      </div>
    </div>
  );
}
